import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import { useAuth0 } from '../../contexts/auth0-context'
import { RootState } from '../../redux/types'
import { Activity } from './useGetActivitiesQuery'

export const useReorderActivitiesMutation = () => {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()

    const strategyId = useSelector((state: RootState) => state.strategy?._id)
    const queryKey = ['activities', strategyId]

    const reorderActivities = async (activities: Activity[]) => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        const { url, method } = endpoints.reorderActivities
        const response = await callAPI(
            url,
            method,
            token,
            activities.map(({ _id }) => _id)
        )

        if (!response?.success) {
            throw new Error(response?.error ?? 'Failed to save the new order')
        }

        return response
    }

    return useMutation(reorderActivities, {
        onMutate: async (activities) => {
            await queryClient.cancelQueries(queryKey)

            const previous = queryClient.getQueryData<Activity[]>(queryKey)

            queryClient.setQueryData(
                queryKey,
                activities.map((activity, index) => ({ ...activity, order: index }))
            )

            return { previous }
        },
        onError: (_error, _variables, context) => {
            const previous = (context as { previous?: Activity[] } | undefined)?.previous

            if (previous) {
                queryClient.setQueryData(queryKey, previous)
            }
        },
        onSettled: () => queryClient.invalidateQueries(queryKey),
    })
}
